import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast
} from '@chakra-ui/react'
import { format, parseISO } from 'date-fns'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import {
  AiOutlineClose,
  AiOutlineEye,
  AiOutlineEyeInvisible
} from 'react-icons/ai'
import { BsCheckLg } from 'react-icons/bs'
import { FaTruckLoading } from 'react-icons/fa'
import { MdEdit, MdSave } from 'react-icons/md'
import { decodeToken } from 'react-jwt'

import useFollowUpAlert from '@hook/alerts/use-followup'
import useTable from '@hook/shared/use-table'

import alert from '@service/alert'

import TableGridItemAlertFollowUp from './item-followup'
import TableGridFooterFollowUp from './item-footer'

// ─── Role check ───────────────────────────────────────────────────────────────
function useCanEdit(): boolean {
  const [canEdit, setCanEdit] = useState(false)
  useEffect(() => {
    try {
      const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID
      const audience = process.env.REACT_APP_AUTH0_AUDIENCE
      const key = `@@auth0spajs@@::${clientId}::${audience}::openid profile email offline_access`
      const raw = localStorage.getItem(key)
      if (!raw) return
      const token = (decodeToken(JSON.parse(raw)?.body?.access_token) as any)
        ?.role
      setCanEdit(
        token === 'Administrador' ||
          token === 'Comprador' ||
          token === 'AnalistaCompra'
      )
    } catch {
      setCanEdit(false)
    }
  }, [])
  return canEdit
}

// ─── Edit row state ────────────────────────────────────────────────────────────
type EditRow = {
  idDocument: number
  idProduct: number
  idStore: number
  supplierId: number
  nomeProduto: string
  currentQuantity: number | null
  currentDate: string
  newQuantity: string
  newExpectedDeliveryDate: string
}

// ─── Modal de edição ──────────────────────────────────────────────────────────
const EditModal: FC<{
  isOpen: boolean
  onClose: () => void
  pedidoInfo: {
    pedido: string | number
    dataCriacao: string
    fornecedor: string
    count: number
  }
  initialRows: EditRow[]
  onSaved: () => void
}> = ({ isOpen, onClose, pedidoInfo, initialRows, onSaved }) => {
  const toast = useToast()
  const [rows, setRows] = useState<EditRow[]>([])
  const [loading, setLoading] = useState(false)
  const [batchDate, setBatchDate] = useState('')
  const initialized = useRef(false)

  // Reset state when modal opens with fresh rows
  useEffect(() => {
    if (isOpen && !initialized.current) {
      setRows(initialRows.map(r => ({ ...r })))
      initialized.current = true
    }
    if (!isOpen) {
      initialized.current = false
      setBatchDate('')
    }
  }, [isOpen, initialRows])

  const updateRow = (
    idx: number,
    field: 'newQuantity' | 'newExpectedDeliveryDate',
    value: string
  ) => {
    setRows(prev => {
      const next = [...prev]
      next[idx] = { ...next[idx], [field]: value }
      return next
    })
  }

  const handleSave = async () => {
    const itensAlterados = rows
      .filter(r => r.newQuantity !== '' || r.newExpectedDeliveryDate !== '')
      .map(r => ({
        productId: r.idProduct,
        novaQuantidade: r.newQuantity !== '' ? Number(r.newQuantity) : null,
        novaDataEntrega: r.newExpectedDeliveryDate || null
      }))

    if (itensAlterados.length === 0) {
      toast({
        title: 'Nenhuma alteração para salvar',
        status: 'warning',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right'
      })
      return
    }

    const first = rows[0]
    setLoading(true)
    try {
      await alert.revisaoCompradorFollowUp({
        documentId: first.idDocument,
        storeId: first.idStore,
        supplierId: first.supplierId,
        itens: itensAlterados
      })
      toast({
        title: `${itensAlterados.length} item(s) atualizado(s)`,
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right'
      })
      onSaved()
      onClose()
    } catch {
      toast({
        title: 'Erro ao salvar alterações',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'bottom-right'
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      scrollBehavior="inside"
      size="4xl"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader
          px={4}
          py={3}
          bg="#1d5840"
          color="white"
          borderTopRadius={5}
          borderBottom="3px solid #ECC94B"
        >
          <Flex align="center" gap={3}>
            <Icon as={MdEdit} boxSize={5} />
            <Box>
              <Text fontSize="14px" fontWeight="700">
                Editar Pedido — {pedidoInfo.pedido}
              </Text>
              <Flex gap={2} mt="2px" align="center" wrap="wrap">
                <Text
                  as="span"
                  fontSize="11px"
                  color="whiteAlpha.800"
                  fontWeight="400"
                >
                  {pedidoInfo.dataCriacao}
                </Text>
                <Text as="span" fontSize="11px" color="whiteAlpha.500">
                  ·
                </Text>
                <Text
                  as="span"
                  fontSize="11px"
                  color="whiteAlpha.800"
                  fontWeight="400"
                >
                  {pedidoInfo.fornecedor}
                </Text>
                <Text as="span" fontSize="11px" color="whiteAlpha.500">
                  ·
                </Text>
                <Badge
                  bg="whiteAlpha.300"
                  color="white"
                  borderRadius="full"
                  px={2}
                  fontSize="10px"
                >
                  {pedidoInfo.count} item{pedidoInfo.count !== 1 ? 's' : ''}
                </Badge>
              </Flex>
            </Box>
          </Flex>
        </ModalHeader>
        <ModalCloseButton color="white" top={3} />

        <ModalBody bg="white" py={4} px={4}>
          {/* Aplicar data em lote */}
          <Flex
            align="center"
            gap={2}
            mb={4}
            p={3}
            bg="gray.50"
            borderRadius="md"
            border="1px solid"
            borderColor="gray.200"
          >
            <Box
              as="span"
              fontSize="12px"
              fontWeight="600"
              color="gray.600"
              whiteSpace="nowrap"
              padding={5}
            >
              Aplicar data em lote:
            </Box>
            <Input
              size="sm"
              type="date"
              w="160px"
              borderColor="gray.300"
              borderRadius="md"
              value={batchDate}
              onChange={e => setBatchDate(e.target.value)}
              _focus={{
                borderColor: '#1d5840',
                boxShadow: '0 0 0 1px #1d5840'
              }}
            />
            <Button
              size="sm"
              bg="#1d5840"
              color="white"
              borderRadius="md"
              _hover={{ bg: '#0f5238' }}
              isDisabled={!batchDate}
              onClick={() => {
                setRows(prev =>
                  prev.map(r => ({ ...r, newExpectedDeliveryDate: batchDate }))
                )
              }}
            >
              Aplicar a todos
            </Button>
            {batchDate && (
              <Button
                size="sm"
                variant="ghost"
                colorScheme="red"
                borderRadius="md"
                onClick={() => {
                  setBatchDate('')
                  setRows(prev =>
                    prev.map(r => ({ ...r, newExpectedDeliveryDate: '' }))
                  )
                }}
              >
                Limpar
              </Button>
            )}
          </Flex>

          <Text fontSize="12px" color="gray.500" mb={3}>
            Preencha apenas os campos que deseja alterar. Campos vazios não
            serão enviados.
          </Text>
          <Box overflowX="auto">
            <Table size="sm">
              <Thead bg="gray.50">
                <Tr>
                  <Th>Produto</Th>
                  <Th isNumeric>Qtd. Atual</Th>
                  <Th isNumeric>Nova Qtd.</Th>
                  <Th>Data Atual</Th>
                  <Th>Nova Data Prevista</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((row, idx) => (
                  <Tr key={`${row.idDocument}-${row.idProduct}`}>
                    <Td>
                      <Text fontWeight="600" fontSize="13px" color="gray.800">
                        {row.nomeProduto}
                      </Text>
                      <Text fontSize="11px" color="gray.400">
                        Cód. {row.idProduct}
                      </Text>
                    </Td>
                    <Td isNumeric>
                      <Text fontSize="13px" color="gray.500" fontWeight="500">
                        {row.currentQuantity != null
                          ? row.currentQuantity.toLocaleString('pt-BR')
                          : '—'}
                      </Text>
                    </Td>
                    <Td isNumeric>
                      <Input
                        size="sm"
                        type="number"
                        w="100px"
                        textAlign="right"
                        borderColor="gray.300"
                        borderRadius="md"
                        placeholder="—"
                        value={row.newQuantity}
                        onChange={e =>
                          updateRow(idx, 'newQuantity', e.target.value)
                        }
                        _focus={{
                          borderColor: '#1d5840',
                          boxShadow: '0 0 0 1px #1d5840'
                        }}
                      />
                    </Td>
                    <Td>
                      <Text fontSize="13px" color="gray.500" fontWeight="500">
                        {row.currentDate
                          ? new Date(row.currentDate).toLocaleDateString(
                              'pt-BR'
                            )
                          : '—'}
                      </Text>
                    </Td>
                    <Td>
                      <Input
                        size="sm"
                        type="date"
                        w="150px"
                        borderColor="gray.300"
                        borderRadius="md"
                        value={row.newExpectedDeliveryDate}
                        onChange={e =>
                          updateRow(
                            idx,
                            'newExpectedDeliveryDate',
                            e.target.value
                          )
                        }
                        _focus={{
                          borderColor: '#1d5840',
                          boxShadow: '0 0 0 1px #1d5840'
                        }}
                      />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </ModalBody>

        <ModalFooter bg="white" borderBottomRadius={5} gap={2}>
          <Button
            variant="outline"
            borderColor="gray.300"
            color="gray.600"
            onClick={onClose}
            isDisabled={loading}
          >
            Cancelar
          </Button>
          <Button
            bg="#1d5840"
            color="white"
            leftIcon={<Icon as={MdSave} />}
            _hover={{ bg: '#0f5238' }}
            isLoading={loading}
            loadingText="Salvando..."
            onClick={handleSave}
          >
            Salvar Alterações
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

// ─── Collapse row ─────────────────────────────────────────────────────────────
const TableGridCollpseFollowUp: FC<{ filterDate: string }> = ({
  filterDate
}) => {
  const { data: rows } = useTable()
  const {
    atrasadoCheck,
    atencaoCheck,
    noprazoCheck,
    handleSelectAllFilter,
    getDatas
  } = useFollowUpAlert()
  const canEdit = useCanEdit()

  const { isOpen: isCollapseOpen, onToggle } = useDisclosure()
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose
  } = useDisclosure()

  const ref = '#collapseOne'
  const attributes = ref ? { href: ref } : {}

  const firstObject = useMemo(() => {
    return rows.data.filter((x: any) => x['general-pedido'] === filterDate)[0]
  }, [rows, filterDate])

  const rowsMap = useMemo(() => {
    return rows.data.filter((x: any) => x['general-pedido'] === filterDate)
  }, [rows, filterDate])

  const count = useMemo(() => {
    return rows.data.filter((x: any) => x['general-pedido'] === filterDate)
      .length
  }, [rows, filterDate])

  const isVisible = useMemo(() => {
    if (firstObject['general-status-entrega'] === 2) {
      return !!atencaoCheck
    } else if (firstObject['general-status-entrega'] === 3) {
      return !!noprazoCheck
    } else {
      return !!atrasadoCheck
    }
  }, [firstObject, atrasadoCheck, noprazoCheck, atencaoCheck])

  const editRows: EditRow[] = useMemo(
    () =>
      rowsMap.map((row: any) => {
        const revisedQty = row['general-a-fornecer-revisado-raw']
        const revisedDate = row['general-data-revisada-raw']
        return {
          idDocument: row['general-pedido'],
          idProduct: row['general-codigo-do-produto'],
          idStore: row['general-centro'],
          supplierId: row['general-supplier-id'],
          nomeProduto: row['general-nome-do-produto'],
          currentQuantity: row['general-a-fornecer-raw'] ?? null,
          currentDate: row['general-data-estimada'] ?? '',
          newQuantity: revisedQty != null ? String(revisedQty) : '',
          newExpectedDeliveryDate: revisedDate
            ? String(revisedDate).slice(0, 10)
            : ''
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterDate]
  )

  const pedidoInfo = useMemo(
    () => ({
      pedido: firstObject['general-pedido'],
      dataCriacao: format(
        parseISO(firstObject['general-data-criacao']),
        'dd/MM/yyyy'
      ),
      fornecedor: firstObject['general-fornecedor-descricao'],
      count
    }),
    [firstObject, count]
  )

  return (
    <>
      {isVisible ? (
        <>
          <tr {...attributes} className="custom-display">
            <td align="center" valign="top" className="tr-gray">
              <Checkbox
                key={1}
                colorScheme="yellow"
                borderColor="blackAlpha.400"
                alignItems="center"
                onChange={() => {
                  handleSelectAllFilter(filterDate)
                }}
              />
            </td>
            <td
              align="center"
              valign="top"
              className="tr-gray"
              style={{ cursor: 'pointer' }}
              onClick={onToggle}
            >
              {isCollapseOpen ? (
                <AiOutlineEyeInvisible size={20} />
              ) : (
                <AiOutlineEye size={20} />
              )}
            </td>
            <td colSpan={3} className="tr-gray">
              <b>
                {' '}
                {format(
                  parseISO(firstObject['general-data-criacao']),
                  'dd/MM/yyyy'
                )}{' '}
                - {firstObject['general-pedido']} -{' '}
                {firstObject['general-fornecedor-descricao']} - ({count})
              </b>
            </td>
            <td className="tr-gray" align="center">
              <FaTruckLoading
                size={20}
                color={
                  firstObject['general-status-entrega'] === 2
                    ? '#3CB371'
                    : firstObject['general-status-entrega'] === 3
                    ? '#F0E68C'
                    : '#FA8072'
                }
              />
            </td>
            <td className="tr-gray" align="center">
              {firstObject['general-status'] === 1 ? (
                <AiOutlineClose color="black" size={25} />
              ) : (
                <BsCheckLg color="green" size={25} />
              )}
            </td>
            <td className="tr-gray" align="center">
              {canEdit && (
                <Tooltip
                  label="Editar itens do pedido"
                  hasArrow
                  placement="top"
                >
                  <Box
                    as="button"
                    onClick={(e: any) => {
                      e.preventDefault()
                      e.stopPropagation()
                      onEditOpen()
                    }}
                    display="inline-flex"
                    alignItems="center"
                    justifyContent="center"
                    w="26px"
                    h="26px"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="green.300"
                    color="green.700"
                    bg="green.50"
                    _hover={{ bg: 'green.100', borderColor: 'green.500' }}
                    transition="all 0.15s"
                  >
                    <Icon as={MdEdit} boxSize="14px" />
                  </Box>
                </Tooltip>
              )}
            </td>
            <td colSpan={6} className="tr-gray"></td>
          </tr>

          {isCollapseOpen ? (
            rows.data.length === 0 ? (
              <tr>
                <td colSpan={12} align="center">
                  Dados não encontrados
                </td>
              </tr>
            ) : (
              <>
                {rowsMap.map((row: any, key: number) => (
                  <TableGridItemAlertFollowUp
                    data={row}
                    key={`table-grid-${key + 1}`}
                    indexData={key}
                  />
                ))}
                <TableGridFooterFollowUp filterDate={filterDate} />
              </>
            )
          ) : (
            <></>
          )}
        </>
      ) : (
        <></>
      )}

      <EditModal
        isOpen={isEditOpen}
        onClose={onEditClose}
        pedidoInfo={pedidoInfo}
        initialRows={editRows}
        onSaved={getDatas}
      />
    </>
  )
}

export default TableGridCollpseFollowUp
